import { Loader2, Volume2 } from 'lucide-react';
import { useEveVoice, EveState } from '@/hooks/useEveVoice';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface EveVoiceShellProps {
  shopId?: string;
}

const ERROR_LABELS: Record<string, string> = {
  unsupported: 'Browser does not support speech recognition',
  mic_denied: 'Microphone access denied',
  engine_failed: 'Speech engine recovering, retrying…',
};

export function EveVoiceShell({ shopId }: EveVoiceShellProps) {
  const { data: isAdmin, isLoading } = useIsInternalAdmin();

  if (isLoading || !isAdmin) return null;

  return <EveVoiceShellInner shopId={shopId} />;
}

function EveVoiceShellInner({ shopId }: EveVoiceShellProps) {
  const { state, error, lastMessage, cancel } = useEveVoice({ shopId, enabled: true });

  if (state === 'disabled') return null;

  const isInterruptible = state === 'greeting' || state === 'recording' || state === 'processing' || state === 'speaking';
  const isClickable = isInterruptible || state === 'listening' || state === 'error';

  const handleClick = () => {
    if (isClickable) {
      cancel();
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleClick}
            disabled={!isClickable}
            data-floating-control="eve"
            className={cn(
              'fixed bottom-14 right-3 z-50 flex items-center justify-center',
              'h-3.5 w-3.5 rounded-full transition-all duration-300 border-0 p-0',
              isClickable && 'cursor-pointer hover:scale-150 hover:ring-2 hover:ring-background/50',
              !isClickable && 'cursor-default',
              state === 'listening' && 'bg-emerald-500/80 animate-pulse',
              state === 'greeting' && 'bg-destructive animate-pulse',
              state === 'recording' && 'bg-destructive animate-pulse',
              state === 'processing' && 'bg-muted-foreground',
              state === 'speaking' && 'bg-primary',
              state === 'error' && 'bg-muted-foreground/50',
            )}
            aria-label={isClickable ? 'Reset EVE' : `EVE status: ${state}`}
          >
            {state === 'processing' && (
              <Loader2 className="h-2.5 w-2.5 animate-spin text-background" />
            )}
            {state === 'speaking' && (
              <Volume2 className="h-2.5 w-2.5 text-primary-foreground" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-[200px]">
          <p className="text-xs font-medium">
            EVE: {state}
            {error && ` — ${ERROR_LABELS[error] || error}`}
          </p>
          {isClickable && (
            <p className="text-xs text-muted-foreground mt-0.5">Click to reset</p>
          )}
          {lastMessage && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {lastMessage}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
